import { actionCreator, namespacedActionCreator } from '@ardoq/rxbeach';
import type { Vector } from '@ardoq/graph';
import type { ZoomActions } from 'tabview/assistantShared/useZoomActions';
import type { AqBlocks, ItemSet } from 'tabview/visualizations/aq-blocks/types';
import type { ContextMenuView } from './contextMenu';

// Toolbar view actions. The view-pane header dispatches these (wired via the view
// context in viewModel$); the view component subscribes and drives the imperative
// aq-blocks element.
export const flexibleBlockDiagramZoomIn = actionCreator(
  '[flexibleBlockDiagram] zoom in'
);
export const flexibleBlockDiagramZoomOut = actionCreator(
  '[flexibleBlockDiagram] zoom out'
);
export const flexibleBlockDiagramFit = actionCreator(
  '[flexibleBlockDiagram] fit to window'
);

/** Namespaced by view instance id, so each mounted diagram has its own read-only state. */
export const flexibleBlockDiagramSetReadOnly = namespacedActionCreator<boolean>(
  '[flexibleBlockDiagram] set read only'
);

export const FLEXIBLE_BLOCK_DIAGRAM_ZOOM_ACTIONS: ZoomActions = {
  zoomIn: flexibleBlockDiagramZoomIn,
  zoomOut: flexibleBlockDiagramZoomOut,
  zoomFit: flexibleBlockDiagramFit,
};

/**
 * Collapse-all and expand-all, alongside the setting the buttons also write.
 *
 * The setting is what the graph is built from. These apply the same change to the groups on screen
 * straight away.
 *
 * Actions rather than the setting alone because a second identical click writes a state that is
 * deep-equal to the current one, and `viewpointModeSettingsForAllViews$` drops it
 * (`distinctUntilChanged(isEqual)`). Nothing reaches the view, so the click does nothing - which
 * is reachable as soon as the setting and the canvas disagree, and a manual expander click makes
 * them disagree without recording anything. An action is not deduped.
 *
 * They also survive a mode disagreement. `updateViewSettings` routes writes on `isViewpointMode`,
 * which presentations contradict on every viewpoint mode slide, while the read path is
 * deliberately mode-robust - so the write can be misrouted where the action still arrives.
 */
export const flexibleBlockDiagramExpandAll = actionCreator(
  '[flexibleBlockDiagram] expand all'
);
export const flexibleBlockDiagramCollapseAll = actionCreator(
  '[flexibleBlockDiagram] collapse all'
);

type SetCollapsedGroupIdsArgs = {
  collapsedGroupIds: string[];
};
export const flexibleBlockDiagramSetCollapsedGroupIds =
  actionCreator<SetCollapsedGroupIdsArgs>(
    '[flexibleBlockDiagram] set collapsed group ids'
  );

export type BlocksContextMenuRequest = {
  component: AqBlocks;
  items: ItemSet;
  client: Vector;
  view: ContextMenuView;
};
export const blocksContextMenuRequested =
  actionCreator<BlocksContextMenuRequest>(
    '[flexibleBlockDiagram] context menu requested'
  );
